// plugins/route-patch.client.js
import { defineNuxtPlugin, useRouter } from '#app';

export default defineNuxtPlugin((nuxtApp) => {
  const router = useRouter();
  const auth = nuxtApp.$auth;
  const allowedDomains = [
    'app','jigsaw-research','wwf','tlacuache-blue',
    'rysense','natwest','sage','sage-earth','sgn'
  ];


  // 1️⃣ Patch router.push & replace to prefix name→theme-...
  function rewriteName(to) {
    if (
        to && typeof to === 'object' &&
        typeof to.name === 'string' &&
        !to.name.startsWith('theme-') &&
        !to.name.startsWith('redirector')
    ) {
      const newName = `theme-${to.name}`;

      return { ...to, name: newName };
    }
    return to;
  }

  const _push = router.push.bind(router);
  router.push = (to, onComplete, onAbort) => {
    return _push(rewriteName(to), onComplete, onAbort);
  };

  const _replace = router.replace.bind(router);
  router.replace = (to, onComplete, onAbort) => {
    return _replace(rewriteName(to), onComplete, onAbort);
  };

  // 2️⃣ beforeEach: mirror handleUrlRedirect
  router.beforeEach((to, from, next) => {
    const domain           = window.location.host.split('.')[0];
    const isAuthenticated  = Boolean(auth?.loggedIn);

    // only run if our domain is in the allowed list
    if (!allowedDomains.includes(domain)) {
      return next();
    }

    let usersTheme = isAuthenticated
        ? (to.params.theme || auth.user.theme?.slug || 'app')
        : (to.params.theme || 'app');

    // preserve task/participant logic
    const isTask        = from.name?.includes('task');
    const isParticipant = from.name?.includes('participant-task');
    const assumedTheme  = from.params.theme;
    if (isTask && isParticipant && assumedTheme && assumedTheme !== usersTheme) {
      usersTheme = assumedTheme;
    }

    const name     = to.name;
    const fullPath = to.fullPath;

    // named-route redirect for admin/participant
    if (name?.startsWith('admin') || name?.startsWith('participant')) {
      const newName = `theme-${name}`;
      return next({
        name:   newName,
        params: { ...to.params, theme: usersTheme },
        query:  to.query
      });
    }
    // path-based catches
    if (fullPath.startsWith('/admin/accounts')) {
      const newPath = fullPath.replace(
          '/admin/accounts',
          `/${usersTheme}/admin/accounts`
      );
      return next(newPath);
    }
    if (fullPath.startsWith('/participant')) {
      const newPath = fullPath.replace(
          '/participant',
          `/${usersTheme}/participant`
      );
      return next(newPath);
    }
    if (fullPath.startsWith('/email-login')) {
      const newPath = fullPath.replace(
          '/email-login',
          `/${usersTheme}/email-login`
      );
      return next(newPath);
    }
    // fallback for authenticated users missing the theme prefix
    if (
        isAuthenticated &&
        !fullPath.startsWith(`/${usersTheme}`) &&
        fullPath !== '/redirector'
    ) {
      const isRoot = fullPath === '/';
      const newPath = fullPath.replace(/^\/[^\/]+/, usersTheme);

      return next(isRoot ? `/${usersTheme}` : `/${newPath}`);
    }
    return next();
  });

  // 3️⃣ afterEach: keep <html> class in sync
  router.afterEach((to) => {
    const isAuthenticated = Boolean(auth?.loggedIn);
    const usersTheme = isAuthenticated
        ? (to.params.theme || auth.user.theme?.slug || 'app')
        : (to.params.theme || 'app');

    const root = document.documentElement;
    root.className = '';
    root.classList.add(`${usersTheme}-theme`);
  });
});
